import type { SignalStrength } from '@/types/health.types';

interface NetworkInformationLike {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

function getConnection(): NetworkInformationLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

/** Maps live browser network quality to scan signal badge tiers. */
export function getNetworkSignalStrength(): SignalStrength {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'weak';
  }

  const connection = getConnection();
  if (!connection) {
    return 'moderate';
  }

  const { effectiveType, downlink = 0, rtt = 0 } = connection;

  if (
    effectiveType === 'slow-2g' ||
    effectiveType === '2g' ||
    rtt >= 500 ||
    (downlink > 0 && downlink < 0.5)
  ) {
    return 'weak';
  }

  if (
    effectiveType === '3g' ||
    rtt >= 200 ||
    (downlink > 0 && downlink < 2)
  ) {
    return 'moderate';
  }

  return 'strong';
}

export function subscribeToNetworkSignalChanges(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const connection = getConnection();
  const handleChange = (): void => onChange();

  window.addEventListener('online', handleChange);
  window.addEventListener('offline', handleChange);
  connection?.addEventListener?.('change', handleChange);

  return () => {
    window.removeEventListener('online', handleChange);
    window.removeEventListener('offline', handleChange);
    connection?.removeEventListener?.('change', handleChange);
  };
}
